import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { isNil } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    ModalHeader,
    ModalTitle,
    ModalBody,
    ModalActions
} from 'app/components/core/Modal';
import { Button, ButtonSize, ButtonVariant } from 'app/components/core/Button';
import * as styles from './StatusDialog.css';

const cx = classnames.bind(styles);

export const DialogType = {
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
    HELP: 'help'
};

const getIconVariant = (type) => {
    switch (type) {
        case DialogType.ERROR:
            return 'status-alert';
        case DialogType.WARNING:
            return 'status-warning';
        case DialogType.HELP:
            return 'help-outline';
        default:
            return null;
    }
};

export function StatusDialog({
    testId = 'alert-dialog',
    type = DialogType.INFO,
    onClose = null,
    title = '',
    message = '',
    children,
    submitButtonLabel = 'OK',
    onSubmit = null,
    isProcessing = false,
    keepOpenAfterSubmit = false,
    width = 400,
    showCancelButton = false
}) {
    const { t } = useTranslation();
    const iconVariant = getIconVariant(type);
    const handleSubmit = () => {
        onSubmit && onSubmit();
        !keepOpenAfterSubmit && onClose && onClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    const isWarningDialog = type === DialogType.WARNING;
    const isHelpDialog = type === DialogType.HELP;
    const hideCloseButton = !showCancelButton && (!isWarningDialog || isNil(onClose));
    return (
        <Dialog
            onKeyDown={handleKeyDown}
            testId={testId}
            width={width}
            onClose={onClose}
            className={cx('base', type)}>
            <ModalHeader
                className={cx('header')}
                icon={iconVariant}
                hideCloseButton={hideCloseButton}>
                <ModalTitle>{title}</ModalTitle>
            </ModalHeader>
            <ModalBody className={styles.message}>{message || children}</ModalBody>
            <ModalActions as="footer">
                {!hideCloseButton && (
                    <Button size={ButtonSize.MEDIUM} onClick={onClose}>
                        {t('common:cancel')}
                    </Button>
                )}
                <Button
                    variant={isHelpDialog ? ButtonVariant.HELP : ButtonVariant.PRIMARY}
                    size={ButtonSize.MEDIUM}
                    isProcessing={isProcessing}
                    onClick={handleSubmit}>
                    {submitButtonLabel}
                </Button>
            </ModalActions>
        </Dialog>
    );
}

StatusDialog.propTypes = {
    testId: PropTypes.string,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    title: PropTypes.string,
    message: PropTypes.node,
    submitButtonLabel: PropTypes.string,
    width: PropTypes.number,
    isProcessing: PropTypes.bool,
    keepOpenAfterSubmit: PropTypes.bool,
    type: PropTypes.oneOf(Object.values(DialogType)),
    showCancelButton: PropTypes.bool
};
